import React from 'react';

export const PageSkeleton = () => {
  return (
    <div className="w-full min-h-screen bg-slate-50 dark:bg-dark-900 transition-colors duration-200">
      {/* Premium Hero Section Skeleton */}
      <div className="relative overflow-hidden bg-slate-100 dark:bg-dark-800/40 border-b border-slate-200/60 dark:border-dark-700/40 py-16 sm:py-20 md:py-24 animate-pulse">
        {/* Subtle decorative elements matching premium vibe */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-light/10 dark:bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange/5 dark:bg-orange/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Breadcrumb or Small tag */}
          <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded-full w-24 mx-auto mb-6" />
          
          {/* Page Title */}
          <div className="h-10 sm:h-12 bg-slate-300 dark:bg-dark-600 rounded-lg w-3/4 max-w-2xl mx-auto mb-6" />
          
          {/* Subtitle */}
          <div className="h-4 sm:h-5 bg-slate-200 dark:bg-dark-700 rounded w-1/2 max-w-lg mx-auto mb-8" />
          
          {/* Call to Action Button / Search bar */}
          <div className="h-12 bg-slate-200 dark:bg-dark-700 rounded-full w-40 mx-auto" />
        </div>
      </div>

      {/* Grid Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        {/* Section title skeleton */}
        <div className="mb-10 text-center animate-pulse">
          <div className="h-8 bg-slate-300 dark:bg-dark-600 rounded w-48 mx-auto mb-3" />
          <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-64 mx-auto" />
        </div>

        {/* 3-Column Premium Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="flex flex-col h-full overflow-hidden rounded-2xl border border-slate-100 dark:border-dark-700/50 bg-white dark:bg-[#161D30] p-6 sm:p-8 shadow-sm dark:shadow-none animate-pulse"
            >
              {/* Card Icon circle mockup */}
              <div className="w-12 h-12 rounded-2xl bg-slate-200 dark:bg-dark-700 mb-6 flex items-center justify-center">
                <div className="w-6 h-6 rounded-md bg-slate-300 dark:bg-dark-600" />
              </div>
              
              {/* Card Title */}
              <div className="h-6 bg-slate-300 dark:bg-dark-600 rounded w-3/4 mb-4" />
              
              {/* Card Description Paragraph Lines */}
              <div className="space-y-2.5 flex-grow">
                <div className="h-3.5 bg-slate-200 dark:bg-dark-700 rounded w-full" />
                <div className="h-3.5 bg-slate-200 dark:bg-dark-700 rounded w-11/12" />
                <div className="h-3.5 bg-slate-200 dark:bg-dark-700 rounded w-5/6" />
                <div className="h-3.5 bg-slate-200 dark:bg-dark-700 rounded w-2/3" />
              </div>
              
              {/* Card Footer Button Link */}
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-1/4 mt-8" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const BlogPostSkeleton = () => {
  return (
    <div className="w-full min-h-screen bg-slate-50 dark:bg-dark-900 py-8 sm:py-12 transition-colors duration-200">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        
        {/* Back Button Skeleton */}
        <div className="mb-6 sm:mb-8 animate-pulse">
          <div className="w-32 h-10 rounded-md bg-slate-200 dark:bg-dark-800" />
        </div>

        {/* Main Post Content Box Skeleton */}
        <div className="overflow-hidden rounded-2xl border border-slate-100 dark:border-white/5 bg-white dark:bg-[#161D30] shadow-md animate-pulse">
          
          {/* Post Image Skeleton */}
          <div className="h-[250px] sm:h-[320px] md:h-[450px] w-full bg-slate-200 dark:bg-dark-700" />
          
          {/* Content Area */}
          <div className="p-5 sm:p-6 md:p-8">
            
            {/* Post Title */}
            <div className="h-8 sm:h-10 bg-slate-300 dark:bg-dark-600 rounded-md w-3/4 mb-6 sm:mb-8" />
            
            {/* Article Content Paragraphs */}
            <div className="space-y-4 mb-8">
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-full" />
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-11/12" />
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-5/6" />
              
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-full pt-2" />
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-10/12" />
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-4/5" />
              
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-11/12 pt-2" />
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-full" />
              <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-3/4" />
            </div>

            {/* Category Tag */}
            <div className="h-6 bg-slate-200 dark:bg-dark-700 rounded w-28 mt-8" />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ModalSkeleton = () => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="w-full max-w-lg mx-4 rounded-2xl bg-white dark:bg-[#161D30] p-6 sm:p-8 shadow-xl border border-slate-100 dark:border-white/5 animate-pulse">
        {/* Header Close button placeholder */}
        <div className="flex justify-between items-center mb-6">
          <div className="h-6 bg-slate-300 dark:bg-dark-600 rounded w-1/3" />
          <div className="w-6 h-6 bg-slate-200 dark:bg-dark-700 rounded-full" />
        </div>
        
        {/* Form Inputs mock */}
        <div className="space-y-4">
          <div>
            <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-1/4 mb-2" />
            <div className="h-10 bg-slate-100 dark:bg-dark-700/50 rounded w-full" />
          </div>
          <div>
            <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-1/4 mb-2" />
            <div className="h-10 bg-slate-100 dark:bg-dark-700/50 rounded w-full" />
          </div>
          <div>
            <div className="h-4 bg-slate-200 dark:bg-dark-700 rounded w-1/4 mb-2" />
            <div className="h-24 bg-slate-100 dark:bg-dark-700/50 rounded w-full" />
          </div>
        </div>

        {/* Submit button mock */}
        <div className="h-11 bg-slate-300 dark:bg-dark-600 rounded-lg w-full mt-6" />
      </div>
    </div>
  );
};

